/**
 * FieldInsightPanel
 *
 * The "explain this field" chat sidebar that overlays the empty right side of a
 * report window or the portfolio detail tab. It is a thin view over
 * fieldInsightStore: the store owns the streaming run + queue + draft, this
 * component just renders the shared session and forwards user input.
 *
 * Surface: a solid Cursor-style sidebar - its own background, a hairline border
 * and a soft shadow so it reads as a designed panel rather than text floating on
 * the page (in dark mode the old transparent chrome read as a black rectangle).
 * Square panel corners, rounded composer. The parent positions the panel
 * (absolute, right-anchored, full height); we cap the width so the panel covers
 * as little of the report as the design allows and stay centered in that strip.
 *
 * TRADEOFF: a solid surface is NOT click-through. Fields underneath the panel's
 * column can no longer be clicked to re-target the explanation - the user must
 * close the panel (header X) or click a field outside the panel's column. Only
 * the margins the panel does not cover stay click-through (the parent wrapper
 * keeps pointer-events:none; this root re-enables them for the panel itself).
 *
 * Scroll isolation is CSS-only: the messages area is its own overflow container
 * with overscroll-behavior:contain, so a wheel over the panel scrolls the panel
 * and a wheel elsewhere scrolls the page behind it - no global listeners.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowUp, X } from "lucide-react";
import MarkdownRenderer from "./MarkdownRenderer";
import * as fieldInsightStore from "../utils/fieldInsightStore";

const NEAR_BOTTOM_PX = 40;
// Narrower than the strip the parents reserve (480 / min(420px,40%)), so the
// solid panel hides less of the report than the space allotted to it.
const MAX_WIDTH_PX = 360;

export default function FieldInsightPanel({
  sessionKey,
  darkMode,
  visible,
  focusNonce,
  onMinimize,
  fetchWithAuth,
  recordActivity,
  placeholder = "Ask about this field...",
}) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  // Whether the user is pinned near the bottom; controls auto-stick on stream.
  const stickToBottom = useRef(true);

  const primary = darkMode ? "#FFFFFF" : "#0A0A0A";
  const muted = darkMode ? "#9CA3AF" : "#6B7280";
  // Deliberate per-theme surfaces: dark is near-black but a step lighter than
  // the page behind it (#0A0A0A), light is white against the page's grey.
  const theme = darkMode
    ? {
        surface: "#17181A",
        border: "rgba(255,255,255,0.11)",
        divider: "rgba(255,255,255,0.08)",
        bubble: "#232427",
        bubbleBorder: "rgba(255,255,255,0.07)",
        composer: "#202124",
        composerBorder: "rgba(255,255,255,0.14)",
        shadow: "0 10px 30px rgba(0,0,0,0.55)",
        sendBg: "#FFFFFF",
        sendFg: "#0A0A0A",
        sendDisabled: "rgba(255,255,255,0.14)",
      }
    : {
        surface: "#FFFFFF",
        border: "rgba(0,0,0,0.10)",
        divider: "rgba(0,0,0,0.07)",
        bubble: "#F4F4F5",
        bubbleBorder: "rgba(0,0,0,0.06)",
        composer: "#FAFAFA",
        composerBorder: "rgba(0,0,0,0.14)",
        shadow: "0 10px 30px rgba(0,0,0,0.10)",
        sendBg: "#0A0A0A",
        sendFg: "#FFFFFF",
        sendDisabled: "rgba(0,0,0,0.12)",
      };

  // The only label the panel is given is the prompt for its surface
  // ("Ask about this report..."); reuse it, minus the ellipsis, as the header.
  const title = placeholder.replace(/[.…]+\s*$/, "").trim() || "Ask about this field";

  // Subscribe to the shared session; the callback fires immediately with a
  // snapshot and on every store change.
  useEffect(() => {
    const unsub = fieldInsightStore.subscribe(sessionKey, (snap) => {
      setMessages(snap.messages);
    });
    setDraft(fieldInsightStore.getDraft(sessionKey));
    return unsub;
  }, [sessionKey]);

  // Auto-stick to bottom on new content, but only if the user has not scrolled
  // up. Runs before paint so appended tokens do not flash a scroll jump.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottom.current = distance <= NEAR_BOTTOM_PX;
  }, []);

  // Autofocus on mount when visible, and refocus whenever focusNonce changes
  // (a fresh field click bumps the nonce). A field double-click (or opening the
  // panel via the chat button) also magnetizes the thread to the bottom: re-arm
  // stick-to-bottom and jump down so the generated answer streams into view
  // instead of scrolling off below a thread the user had scrolled up in.
  useEffect(() => {
    if (!visible) return;
    textareaRef.current?.focus();
    stickToBottom.current = true;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [visible, focusNonce]);

  const autoGrow = useCallback((el) => {
    if (!el) return;
    el.style.height = "auto";
    // 1-3 rows: clamp to ~3 lines then let the field scroll internally.
    const lineHeight = 20;
    const maxHeight = lineHeight * 3 + 12;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, []);

  useLayoutEffect(() => {
    autoGrow(textareaRef.current);
  }, [draft, visible, autoGrow]);

  const onDraftChange = (e) => {
    const value = e.target.value;
    setDraft(value);
    fieldInsightStore.setDraft(sessionKey, value);
  };

  const submit = useCallback(() => {
    const text = draft.trim();
    if (!text) return;
    fieldInsightStore.sendMessage(sessionKey, { fetchWithAuth, recordActivity, text });
    setDraft("");
    fieldInsightStore.setDraft(sessionKey, "");
    stickToBottom.current = true;
  }, [draft, sessionKey, fetchWithAuth, recordActivity]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  if (!visible) return null;

  const canSend = draft.trim().length > 0;

  return (
    <div
      data-testid="field-insight-panel"
      style={{
        position: "absolute",
        inset: 0,
        maxWidth: MAX_WIDTH_PX,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        boxShadow: theme.shadow,
        color: primary,
        fontFamily: "Inter, sans-serif",
        // Solid surface: the panel owns every click inside its own box (the
        // parent wrapper stays pointer-events:none, so the strip around the
        // panel still passes clicks through to the report underneath).
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          flex: "none",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 8px 7px 14px",
          borderBottom: `1px solid ${theme.divider}`,
        }}
      >
        <span
          data-testid="field-insight-title"
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            fontWeight: 500,
            color: muted,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </span>
        <button
          type="button"
          onClick={onMinimize}
          aria-label="Close"
          title="Close"
          style={{
            flex: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: 6,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: muted,
            padding: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.bubble;
            e.currentTarget.style.color = primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = muted;
          }}
        >
          <X size={14} />
        </button>
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        data-testid="field-insight-thread"
        style={{
          // Fills the panel so the composer stays pinned to the bottom edge of
          // the surface (the old flex-basis:auto hug existed only because the
          // transparent panel had no bottom edge to pin to). minHeight 0 lets
          // flex shrink below the content height so the thread scrolls here.
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          padding: "10px 14px 4px 14px",
          // The window-theme leak, one element over from the composer: App.css
          // paints ::-webkit-scrollbar off a `.dark` ANCESTOR, so this thread grew
          // a dark-slate stripe inside a light panel in a dark window. A
          // pseudo-element cannot be styled inline, so hand the rules the colours.
          "--scrollbar-track": darkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
          "--scrollbar-thumb": darkMode ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)",
          "--scrollbar-thumb-hover": darkMode ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.28)",
        }}
      >
        {messages.map((msg) => {
          if (msg.role === "status") {
            return (
              <div
                key={msg.id}
                data-testid="field-insight-status-message"
                style={{ color: muted, fontStyle: "italic", fontSize: 13, margin: "6px 0" }}
              >
                {msg.content}
              </div>
            );
          }
          if (msg.role === "user") {
            return (
              <div
                key={msg.id}
                data-testid="field-insight-user-message"
                // A raised full-width card, not a chat bubble: the user's own
                // query reads as the heading of the turn, the answer below it.
                style={{
                  color: primary,
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  background: theme.bubble,
                  border: `1px solid ${theme.bubbleBorder}`,
                  borderRadius: 10,
                  padding: "8px 11px",
                  margin: "10px 0 8px 0",
                }}
              >
                {msg.content}
              </div>
            );
          }
          // assistant: plain flowing text on the panel surface, no bubble.
          return (
            <div
              key={msg.id}
              data-testid="field-insight-assistant-message"
              style={{ color: primary, fontSize: 14, lineHeight: 1.6, margin: "8px 0 14px 0" }}
            >
              <MarkdownRenderer markdown={msg.content} darkMode={darkMode} />
              {msg.isStreaming && (
                <span
                  data-testid="field-insight-stream-cursor"
                  className={`inline-block w-2 h-5 ml-0.5 ${
                    darkMode ? "bg-white" : "bg-gray-900"
                  } rounded-sm animate-pulse align-text-bottom`}
                />
              )}
            </div>
          );
        })}
      </div>

      <div style={{ flex: "none", padding: "8px 12px 12px 12px" }}>
        <div
          data-testid="field-insight-composer"
          style={{
            background: theme.composer,
            border: `1px solid ${theme.composerBorder}`,
            borderRadius: 12,
            padding: "8px 8px 6px 10px",
          }}
        >
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={onDraftChange}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={placeholder}
            style={{
              display: "block",
              width: "100%",
              resize: "none",
              // Flush with the composer around it, declared EXPLICITLY rather than
              // left transparent. App.css colours form controls with `!important`
              // off a `.dark` ANCESTOR, so a `transparent` field resolved the
              // WINDOW's theme: ReportPopup renders this panel with
              // darkMode={false} inside its `.dark` wrapper, and the field painted
              // #141414 - a black bar behind the placeholder on a light report
              // sheet. The custom properties are what those rules now read (and the
              // only way to reach ::placeholder, unstylable inline); the plain
              // background keeps the field opaque on its own. Same trap and same
              // fix as --report-toolbar-bg (index.css).
              background: theme.composer,
              "--input-bg": theme.composer,
              "--input-fg": primary,
              "--input-placeholder": muted,
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: primary,
              fontFamily: "inherit",
              fontSize: 14,
              lineHeight: "20px",
              padding: 0,
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
            <button
              type="button"
              onClick={submit}
              disabled={!canSend}
              aria-label="Send"
              title="Send"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                borderRadius: 12,
                border: "none",
                padding: 0,
                cursor: canSend ? "pointer" : "default",
                background: canSend ? theme.sendBg : theme.sendDisabled,
                color: canSend ? theme.sendFg : muted,
              }}
            >
              <ArrowUp size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
